import { useState } from 'react';
import { adminService } from '@/api/services/admin';
import type { User } from '@/api/services/admin';
import { Dialog, DialogContent } from '@/components/ui/Dialog';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';

// ── Tab type ─────────────────────────────────────────────────────────────────

type Tab = 'profile' | 'activity' | 'credits' | 'danger';

// ── Field row ─────────────────────────────────────────────────────────────────

function FieldRow({ label, value, mono = false }: { label: string; value: string | number | boolean; mono?: boolean }) {
  const display = typeof value === 'boolean' ? (value ? 'Yes' : 'No') : String(value);
  return (
    <div>
      <p className="text-xs text-text-secondary uppercase tracking-wider mb-0.5">{label}</p>
      <p className={`text-sm text-text-primary break-all ${mono ? 'font-mono' : ''}`}>{display}</p>
    </div>
  );
}

// ── Profile tab ───────────────────────────────────────────────────────────────

function ProfileTab({ user }: { user: User }) {
  return (
    <div className="grid grid-cols-2 gap-x-6 gap-y-4 p-4">
      <div className="col-span-2">
        <FieldRow label="ID" value={user.id} mono />
      </div>
      <FieldRow label="Email" value={user.email} />
      <FieldRow label="Full name" value={user.full_name || '—'} />
      <FieldRow label="Organization" value={user.org_name || '—'} />
      <FieldRow label="Active" value={user.active} />
      <FieldRow label="Subscribed" value={user.status.subscription.is_subscribed} />
      <FieldRow label="Available credits" value={user.status.subscription.available_credits.toLocaleString()} />
      <div className="col-span-2 border-t border-border pt-4 mt-2">
        <p className="text-xs text-text-secondary uppercase tracking-wider mb-3">Activity counters</p>
        <div className="grid grid-cols-2 gap-x-6 gap-y-3">
          <FieldRow label="FA assessment" value={user.status.fa_assessment} />
          <FieldRow label="FA analysis" value={user.status.fa_analysis} />
          <FieldRow label="Prospect search" value={user.status.prospect_search} />
          <FieldRow label="Prospect research" value={user.status.prospect_research} />
          <FieldRow label="Prospect messages" value={user.status.prospect_messages} />
        </div>
      </div>
    </div>
  );
}

// ── Credits tab ───────────────────────────────────────────────────────────────

function CreditsTab({ user, onChange }: { user: User; onChange: () => void }) {
  const [numCredits, setNumCredits] = useState(15);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  const handleAdd = async () => {
    if (numCredits < 1) { setError('Credits must be at least 1.'); return; }
    setError('');
    setSuccess('');
    setSubmitting(true);
    try {
      const result = await adminService.addCredits(user.id, numCredits);
      const newBalance = result?.available_credits;
      setSuccess(
        newBalance !== undefined
          ? `Granted ${numCredits} credits. New balance: ${newBalance.toLocaleString()}.`
          : `Granted ${numCredits} credits.`,
      );
      setNumCredits(15);
      onChange();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to add credits.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="p-4 space-y-4">
      {/* Current balance */}
      <div className="p-4 rounded-lg bg-surface border border-border">
        <p className="text-xs text-text-secondary uppercase tracking-wider mb-1">Available credits</p>
        <p className="text-4xl font-bold text-text-primary tabular-nums">
          {user.status.subscription.available_credits.toLocaleString()}
        </p>
      </div>

      {/* Inline grant form */}
      <div className="space-y-3">
        <h4 className="text-sm font-medium text-text-primary">Add credits</h4>
        {success && (
          <div className="p-3 bg-success-50 dark:bg-success-900/20 border border-success-200 dark:border-success-800/30 rounded-md">
            <p className="text-sm text-success-700 dark:text-success-400">{success}</p>
          </div>
        )}
        {error && (
          <div className="p-3 bg-error-50 dark:bg-error-900/20 border border-error-200 dark:border-error-800/30 rounded-md">
            <p className="text-sm text-error-700 dark:text-error-400">{error}</p>
          </div>
        )}
        <div className="flex items-center gap-2">
          <input
            type="number"
            min={1}
            value={numCredits}
            onChange={(e) => {
              const n = parseInt(e.target.value, 10);
              setNumCredits(Number.isFinite(n) ? n : 1);
            }}
            disabled={submitting}
            className="w-24 rounded-md border border-border bg-surface px-3 py-2 text-sm text-text-primary focus:outline-none focus:ring-1 focus:ring-primary-500 disabled:opacity-50"
          />
          <Button variant="primary" size="sm" disabled={submitting} onClick={handleAdd}>
            {submitting ? 'Adding…' : 'Add'}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ── Danger tab ────────────────────────────────────────────────────────────────

function DangerTab({ user, onChange }: { user: User; onChange: () => void }) {
  const [toggling, setToggling] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  const handleDisable = async () => {
    if (!window.confirm(`Disable ${user.email}?`)) return;
    setError('');
    setSuccess('');
    setToggling(true);
    try {
      await adminService.disableUser(user.id);
      setSuccess(`${user.email} has been disabled.`);
      onChange();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Action failed.');
    } finally {
      setToggling(false);
    }
  };

  const handleEnable = async () => {
    setError('');
    setSuccess('');
    setToggling(true);
    try {
      await adminService.enableUser(user.id);
      setSuccess(`${user.email} has been enabled.`);
      onChange();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Action failed.');
    } finally {
      setToggling(false);
    }
  };

  return (
    <div className="p-4 space-y-4">
      {success && (
        <div className="p-3 bg-success-50 dark:bg-success-900/20 border border-success-200 dark:border-success-800/30 rounded-md">
          <p className="text-sm text-success-700 dark:text-success-400">{success}</p>
        </div>
      )}
      {error && (
        <div className="p-3 bg-error-50 dark:bg-error-900/20 border border-error-200 dark:border-error-800/30 rounded-md">
          <p className="text-sm text-error-700 dark:text-error-400">{error}</p>
        </div>
      )}

      {user.active ? (
        <div className="space-y-2">
          <Button variant="danger" disabled={toggling} onClick={handleDisable} className="w-full justify-center">
            {toggling ? 'Disabling…' : 'Disable user'}
          </Button>
          <p className="text-xs text-text-muted">Prevents this user from logging in. Reversible.</p>
        </div>
      ) : (
        <div className="space-y-2">
          <Button variant="primary" disabled={toggling} onClick={handleEnable} className="w-full justify-center">
            {toggling ? 'Enabling…' : 'Enable user'}
          </Button>
          <p className="text-xs text-text-muted">Restores access for this user.</p>
        </div>
      )}
    </div>
  );
}

// ── User drawer ───────────────────────────────────────────────────────────────

interface UserDrawerProps {
  user: User | null;
  open: boolean;
  onClose: () => void;
  onChange: () => void;
}

export function UserDrawer({ user, open, onClose, onChange }: UserDrawerProps) {
  const [tab, setTab] = useState<Tab>('profile');

  const TABS: { id: Tab; label: string }[] = [
    { id: 'profile', label: 'Profile' },
    { id: 'activity', label: 'Activity' },
    { id: 'credits', label: 'Credits' },
    { id: 'danger', label: 'Danger' },
  ];

  if (!user) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      {/* Right-side full-height panel */}
      <DialogContent className="fixed top-0 right-0 bottom-0 w-full max-w-md rounded-none rounded-l-xl flex flex-col overflow-hidden m-0">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 p-4 border-b border-border flex-shrink-0">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-base font-semibold text-text-primary">
                {user.full_name || <span className="italic text-text-muted">No name</span>}
              </span>
              <Badge variant={user.active ? 'success' : 'error'} size="xs">
                {user.active ? 'Active' : 'Disabled'}
              </Badge>
            </div>
            <p className="text-sm text-text-secondary mt-0.5 truncate">{user.email}</p>
          </div>
          <button
            onClick={onClose}
            className="text-text-muted hover:text-text-primary transition-colors flex-shrink-0 p-1 rounded hover:bg-surface"
            aria-label="Close drawer"
          >
            ✕
          </button>
        </div>

        {/* Tab row */}
        <div className="flex border-b border-border flex-shrink-0">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`flex-1 py-2.5 text-sm font-medium transition-colors ${
                tab === t.id
                  ? 'text-primary-500 dark:text-primary-400 border-b-2 border-primary-500 dark:border-primary-400'
                  : 'text-text-secondary hover:text-text-primary'
              } ${t.id === 'danger' && tab !== 'danger' ? 'text-error-600 dark:text-error-400' : ''}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Tab content */}
        <div className="flex-1 overflow-y-auto">
          {tab === 'profile' && <ProfileTab user={user} />}

          {tab === 'activity' && (
            <div className="p-4">
              <p className="text-sm text-text-muted italic">
                Lifecycle runs for this user — per-user activity endpoint pending. Needs{' '}
                <code className="font-mono text-xs bg-surface px-1 py-0.5 rounded">
                  GET /adm/get_user/{'{id}'}/activity
                </code>{' '}
                or filter on a runs endpoint.
              </p>
            </div>
          )}

          {tab === 'credits' && <CreditsTab user={user} onChange={onChange} />}
          {tab === 'danger' && <DangerTab user={user} onChange={onChange} />}
        </div>
      </DialogContent>
    </Dialog>
  );
}
